/**
 * Admin Dashboard — permission-gated tile mosaic.
 *
 * Backend (T2 PR-1 #1347) replaced the flat `getDashboardStats` shape
 * with a tile-based response:
 *
 *   GET /v1/admin/analytics/dashboard
 *     → { tiles: Tile[], generated_at: string }
 *     where Tile = { id: string, requires: string, data: any }
 *
 * The backend pre-filters tiles by permission, so this component only
 * needs to render whatever it receives. It DOES NOT re-check permissions
 * (no client-side permission enforcement — that's a security anti-pattern).
 *
 * Auto-refresh every 30s. Adjacent loading + error states keep the page
 * responsive when the Lambda cold-starts or the dashboard cache misses.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-analytics-tabs-design.md §6.
 * Plan: docs/superpowers/plans/2026-05-28-admin-analytics-tabs.md Task 12.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { RefreshCw, ArrowRight } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { StatCardSkeleton } from '@/components/ui/Skeleton';
import { TileRenderer, type Tile } from './components/TileRenderer';
import { WithheldTileCard } from '@/pages/agent/components/Cells';
import type { WithheldTile } from '@/pages/agent/protocol';

interface DashboardResponse {
  tiles: Tile[];
  /**
   * 🔴 TILES THE METRIC KILL SWITCH WITHHELD (spec 8.4, added 2026-08-21).
   *
   * A withheld tile is ABSENT from `tiles[]` rather than sent with null
   * measures, because `QueueTile`/`NsmTile` render through
   * `formatNumber(count)` and `Intl.NumberFormat().format(null)` is the string
   * "0" — a fabricated zero over a number somebody has just decided is wrong
   * and turned off. The FACT is not lost: it travels in this array, and it
   * renders below as an explicit em-dash card that says WHY.
   *
   * Optional on the wire so an older backend renders no cards rather than
   * throwing; `?? []` below does that.
   */
  withheld?: WithheldTile[];
  generated_at: string;
}

// Subset of GET /v1/admin/analytics/growth-science we surface as exec
// command-center headlines (full detail lives on the Growth page).
interface GrowthScience {
  engagement: { dau: number; wau: number; mau: number };
  match_outcomes: Array<{ status: string; count: number }>;
  revenue: Array<{ status: string; count: number; cents: number }>;
}

/** Compact exec headline tile linking through to the Growth page. */
function ExecTile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Link
      to="/growth"
      className="group rounded-lg border bg-card p-4 transition-colors hover:border-primary/50"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </Link>
  );
}

export function DashboardPage() {
  const apiUrl = useApiUrl();
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [withheld, setWithheld] = useState<WithheldTile[]>([]);
  const [science, setScience] = useState<GrowthScience | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (showSkeleton: boolean) => {
      if (showSkeleton) setIsLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/analytics/dashboard`);
        const data: DashboardResponse = await res.json();
        setTiles(data.tiles ?? []);
        setWithheld(data.withheld ?? []);
        setGeneratedAt(data.generated_at);
        // Best-effort exec headlines (requires analytics.growth.read; on 403
        // the command-center row simply doesn't render — fail-soft).
        adminFetch(`${apiUrl}/v1/admin/analytics/growth-science`)
          .then((r) => r.json())
          .then((s: GrowthScience) => setScience(s))
          .catch(() => { /* no growth perm or transient — hide the row */ });
      } catch (e) {
        const msg =
          e instanceof AdminApiError
            ? `Failed to load dashboard (HTTP ${e.status}): ${e.message}`
            : (e as Error)?.message || 'Failed to load dashboard';
        setError(msg);
      } finally {
        setIsLoading(false);
        setRefreshing(false);
      }
    },
    [apiUrl],
  );

  useEffect(() => {
    void load(true);
    const interval = setInterval(() => void load(false), 30_000);
    return () => clearInterval(interval);
  }, [load]);

  if (error && tiles.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          {error}
          <button
            onClick={() => void load(true)}
            className="ml-4 underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && tiles.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Dashboard</h2>
          <p className="text-muted-foreground">Overview of your platform metrics</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">Dashboard</h2>
          <p className="text-muted-foreground">Overview of your platform metrics</p>
        </div>
        <div className="flex items-center gap-3">
          {generatedAt && (
            <span className="text-xs text-muted-foreground">
              Last updated {new Date(generatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => void load(false)}
            disabled={refreshing}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-700">
          Refresh failed: {error} (showing previous values)
        </div>
      )}

      {/* Exec command-center headlines — live Growth-Science highlights,
          each linking through to the Growth page for the full breakdown. */}
      {science && (() => {
        const outcomesTotal = science.match_outcomes.reduce((s, r) => s + r.count, 0);
        const connected = science.match_outcomes.find((r) => r.status === 'connected')?.count ?? 0;
        const connectRate = outcomesTotal ? `${Math.round((connected / outcomesTotal) * 100)}%` : '—';
        const revenueCents = science.revenue.find((r) => r.status === 'completed')?.cents ?? 0;
        return (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <ExecTile label="Connect rate" value={connectRate} sub="real-involved matches" />
            <ExecTile label="Revenue" value={`$${(revenueCents / 100).toLocaleString()}`} sub="realized (match payments)" />
            <ExecTile label="WAU" value={science.engagement.wau.toLocaleString()} sub="real users, 7-day active" />
            <ExecTile label="MAU" value={science.engagement.mau.toLocaleString()} sub="real users, 30-day active" />
          </div>
        );
      })()}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <TileRenderer key={tile.id} tile={tile} />
        ))}
        {/* An em-dash card per withheld tile, never a blank and never a zero. */}
        {withheld.map((tile) => (
          <WithheldTileCard key={`withheld-${tile.tile}`} tile={tile} />
        ))}
      </div>

      {tiles.length === 0 && withheld.length === 0 && !isLoading && (
        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
          No tiles available — you may not have any of the permission keys gating the dashboard.
        </div>
      )}
    </div>
  );
}
